/**
 * API de administración de reportería (§8.2). CRUD de configuraciones +
 * introspección del catálogo real + directorio de roles/usuarios. Bajo la
 * política reporteria.admin (rol Administrador). El interceptor de axios
 * antepone `/api/v1`.
 */
import { axiosClient } from "@/shared/api/axios-client";
import type {
  Catalogo,
  RelacionSugerida,
  RepDefinicion,
  ReporteDefaultDto,
  RepResultadoPagina,
} from "@/features/reporteria/services/reporteria.api";

const A = "/api/reporteria/admin";

// ---- DTOs (espejo del backend) ----

export interface ConfigResumen {
  id: number;
  nombre: string;
  descripcion: string | null;
  tipo: string; // "rol" | "usuario"
  activo: boolean;
  numTablas: number;
  numRoles: number;
  numUsuarios: number;
}

export interface ConfigColumna {
  nombreColumna: string;
  etiqueta: string | null;
  tipoDato: string | null;
  enmascarar: boolean;
  mascara: string | null;
  mostrarSiId: boolean;
}

export interface ConfigTabla {
  esquemaTabla: string;
  nombreTabla: string;
  etiqueta: string | null;
  columnas: ConfigColumna[];
}

export interface ConfigColumnaCalc {
  nombre: string;
  formula: string;
  tipo: string; // "fila" | "agregado"
  formato: string | null;
}

export interface ConfigCompleta {
  id: number | null;
  nombre: string;
  descripcion: string | null;
  tipo: string;
  activo: boolean;
  tablas: ConfigTabla[];
  roles: string[];
  usuarios: string[]; // Guid[]
  columnasCalculadas: ConfigColumnaCalc[];
}

export interface EsquemaTabla {
  esquema: string;
  tabla: string;
  tipo: string;
}

export interface EsquemaColumna {
  esquema: string;
  tabla: string;
  columna: string;
  tipoDato: string;
  esNullable: boolean;
}

export interface UsuarioReporte {
  id: string;
  email: string;
  nombre: string | null;
}

// ---- CRUD de configuraciones ----

export async function listarConfiguraciones(): Promise<ConfigResumen[]> {
  const { data } = await axiosClient.get<ConfigResumen[]>(`${A}/configuraciones`);
  return data;
}

export async function obtenerConfiguracion(id: number): Promise<ConfigCompleta> {
  const { data } = await axiosClient.get<ConfigCompleta>(`${A}/configuraciones/${id}`);
  return data;
}

export async function guardarConfiguracion(cfg: ConfigCompleta): Promise<number> {
  const { data } = await axiosClient.post<{ id: number }>(`${A}/configuraciones`, cfg);
  return data.id;
}

export async function eliminarConfiguracion(id: number): Promise<void> {
  await axiosClient.delete(`${A}/configuraciones/${id}`);
}

// ---- Introspección del catálogo real ----

export async function introspeccionTablas(): Promise<EsquemaTabla[]> {
  const { data } = await axiosClient.get<EsquemaTabla[]>(`${A}/introspeccion/tablas`);
  return data;
}

export async function introspeccionColumnas(esquema: string, tabla: string): Promise<EsquemaColumna[]> {
  const { data } = await axiosClient.get<EsquemaColumna[]>(
    `${A}/introspeccion/columnas?esquema=${encodeURIComponent(esquema)}&tabla=${encodeURIComponent(tabla)}`
  );
  return data;
}

// ---- Directorio de Identity ----

export async function listarRoles(): Promise<string[]> {
  const { data } = await axiosClient.get<string[]>(`${A}/roles`);
  return data;
}

export async function listarUsuarios(): Promise<UsuarioReporte[]> {
  const { data } = await axiosClient.get<UsuarioReporte[]>(`${A}/usuarios`);
  return data;
}

// ---- Builder de predefinidos EN CONTEXTO de una configuración (§8.2) ----

export async function defCatalogo(idConfig: number): Promise<Catalogo> {
  const { data } = await axiosClient.get<Catalogo>(`${A}/configuraciones/${idConfig}/catalogo`);
  return data;
}

export async function defRelaciones(idConfig: number): Promise<RelacionSugerida[]> {
  const { data } = await axiosClient.get<RelacionSugerida[]>(`${A}/configuraciones/${idConfig}/relaciones`);
  return data;
}

export async function defValoresColumna(idConfig: number, token: string, max = 50): Promise<unknown[]> {
  const { data } = await axiosClient.get<unknown[]>(
    `${A}/configuraciones/${idConfig}/columnas/${token}/valores?max=${max}`
  );
  return data;
}

export async function defPreview(
  idConfig: number,
  params: { def: RepDefinicion; pagina: number; tamano: number; incluirTotal: boolean; muestra: boolean }
): Promise<RepResultadoPagina> {
  const { data } = await axiosClient.post<RepResultadoPagina>(`${A}/configuraciones/${idConfig}/preview`, params);
  return data;
}

export async function defContar(idConfig: number, def: RepDefinicion): Promise<number> {
  const { data } = await axiosClient.post<{ total: number }>(`${A}/configuraciones/${idConfig}/contar`, def);
  return data.total;
}

export async function defEncolarExportacion(idConfig: number, def: RepDefinicion, titulo: string): Promise<number> {
  const { data } = await axiosClient.post<{ idTrabajo: number }>(`${A}/configuraciones/${idConfig}/exportar`, { def, titulo });
  return data.idTrabajo;
}

export async function defListar(idConfig: number): Promise<ReporteDefaultDto[]> {
  const { data } = await axiosClient.get<ReporteDefaultDto[]>(`${A}/configuraciones/${idConfig}/predefinidos`);
  return data;
}

export async function defObtener(id: number): Promise<RepDefinicion> {
  const { data } = await axiosClient.get<RepDefinicion>(`${A}/predefinidos/${id}`);
  return data;
}

export async function defGuardar(
  idConfig: number,
  body: { id?: number | null; nombre: string; descripcion?: string | null; def: RepDefinicion }
): Promise<number> {
  const { data } = await axiosClient.post<{ id: number }>(`${A}/configuraciones/${idConfig}/predefinidos`, body);
  return data.id;
}

export async function defEliminar(id: number): Promise<void> {
  await axiosClient.delete(`${A}/predefinidos/${id}`);
}
